import React from "react";
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { FolderSLAUserMetrics } from "../types/folderSla.types";

interface FolderSLAUserCardsProps {
  data: FolderSLAUserMetrics[];
  loading?: boolean;
  title?: string;
}

// Safe accessor with default value
const safeNumber = (value: number | undefined | null, defaultValue = 0): number => {
  return typeof value === "number" && !isNaN(value) ? value : defaultValue;
};

const getComplianceColor = (percentage: number) => {
  if (percentage >= 90) return "#10b981";
  if (percentage >= 80) return "#f59e0b";
  if (percentage >= 70) return "#f97316";
  return "#ef4444";
};

const getComplianceColorClass = (percentage: number) => {
  if (percentage >= 90) return "text-green-600";
  if (percentage >= 80) return "text-yellow-600";
  if (percentage >= 70) return "text-orange-600";
  return "text-red-600";
};

const getComplianceBadge = (percentage: number) => {
  if (percentage >= 90) return "Excelente";
  if (percentage >= 80) return "Bueno";
  if (percentage >= 70) return "Regular";
  return "Necesita Mejora";
};

const getComplianceBadgeColor = (percentage: number) => {
  if (percentage >= 90) return "bg-green-100 text-green-800";
  if (percentage >= 80) return "bg-yellow-100 text-yellow-800";
  if (percentage >= 70) return "bg-orange-100 text-orange-800";
  return "bg-red-100 text-red-800";
};

const ProgressBar = ({
  percentage,
  color,
}: {
  percentage: number;
  color: string;
}) => (
  <div className="w-full bg-gray-200 rounded-full h-2">
    <div
      className="h-2 rounded-full transition-all duration-300"
      style={{
        width: `${Math.min(percentage || 0, 100)}%`,
        backgroundColor: color,
      }}
    />
  </div>
);

interface UserCardProps {
  user: FolderSLAUserMetrics;
}

const UserCard: React.FC<UserCardProps> = ({ user }) => {
  const overallCompliancePct = safeNumber(user.overall_compliance_pct);
  const beforeCutoffPct = safeNumber(user.before_cutoff_compliance_pct);
  const afterCutoffPct = safeNumber(user.after_cutoff_compliance_pct);
  const compliantBeforeCutoff = safeNumber(user.compliant_before_cutoff);
  const compliantAfterCutoff = safeNumber(user.compliant_after_cutoff);
  const totalProcessed = safeNumber(user.total_processed);
  const totalCompliant = safeNumber(user.total_compliant);
  const totalNonCompliant = safeNumber(user.total_non_compliant);
  const avgBusinessHours = safeNumber(user.avg_completion_time_business_hours);

  return (
    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 hover:shadow-md transition-shadow duration-200">
      {/* User Header */}
      <div className="flex items-center mb-4">
        <div className="bg-blue-100 rounded-full p-2 mr-3">
          <UserOutlined className="text-blue-600 text-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-lg font-semibold text-gray-900 truncate">
            {user.user_name || "Usuario"}
          </div>
          <div className="text-sm text-gray-500 truncate">{user.user_email || ""}</div>
        </div>
      </div>

      {/* Overall Compliance */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Cumplimiento General
          </span>
          <span
            className={`text-lg font-bold ${getComplianceColorClass(overallCompliancePct)}`}
          >
            {overallCompliancePct.toFixed(1)}%
          </span>
        </div>
        <ProgressBar
          percentage={overallCompliancePct}
          color={getComplianceColor(overallCompliancePct)}
        />
        <div className="mt-2">
          <span
            className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getComplianceBadgeColor(overallCompliancePct)}`}
          >
            {getComplianceBadge(overallCompliancePct)}
          </span>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="space-y-3">
        {/* Before Cutoff */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircleOutlined className="text-green-500 text-sm mr-2" />
            <span className="text-sm text-gray-600">Antes del corte</span>
          </div>
          <span className="text-sm font-medium text-green-600">
            {beforeCutoffPct.toFixed(1)}% ({compliantBeforeCutoff})
          </span>
        </div>

        {/* After Cutoff */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ClockCircleOutlined className="text-blue-500 text-sm mr-2" />
            <span className="text-sm text-gray-600">Después del corte</span>
          </div>
          <span className="text-sm font-medium text-blue-600">
            {afterCutoffPct.toFixed(1)}% ({compliantAfterCutoff})
          </span>
        </div>

        {/* Non-Compliant */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <WarningOutlined className="text-red-500 text-sm mr-2" />
            <span className="text-sm text-gray-600">Fuera de SLA</span>
          </div>
          <span className="text-sm font-medium text-red-600">
            {totalNonCompliant} expedientes
          </span>
        </div>

        {/* Average Time */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <FieldTimeOutlined className="text-purple-500 text-sm mr-2" />
            <span className="text-sm text-gray-600">Tiempo promedio</span>
          </div>
          <span className="text-sm font-medium text-purple-600">
            {avgBusinessHours.toFixed(1)}h
          </span>
        </div>

        {/* Separator */}
        <hr className="border-gray-200" />

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-lg font-bold text-gray-900">
              {totalProcessed}
            </div>
            <div className="text-xs text-gray-500">Total</div>
          </div>
          <div>
            <div className="text-lg font-bold text-green-600">
              {totalCompliant}
            </div>
            <div className="text-xs text-gray-500">En SLA</div>
          </div>
          <div>
            <div className="text-lg font-bold text-red-600">
              {totalNonCompliant}
            </div>
            <div className="text-xs text-gray-500">Fuera SLA</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FolderSLAUserCards: React.FC<FolderSLAUserCardsProps> = ({
  data,
  loading = false,
  title = "Rendimiento Individual",
}) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium mb-4 text-gray-700">{title}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-gray-50 rounded-lg p-5 border border-gray-200 animate-pulse"
            >
              <div className="flex items-center mb-4">
                <div className="bg-gray-200 rounded-full w-10 h-10 mr-3" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
              <div className="h-2 bg-gray-200 rounded w-full mb-4" />
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-200 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium mb-4 text-gray-700">{title}</h2>
        <div className="flex justify-center items-center h-32">
          <p className="text-gray-500">No hay datos de usuarios disponibles</p>
        </div>
      </div>
    );
  }

  const activeUsers = data.filter((user) => safeNumber(user.total_processed) > 0);

  if (activeUsers.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium mb-4 text-gray-700">{title}</h2>
        <div className="flex justify-center items-center h-32">
          <p className="text-gray-500">
            No hay usuarios con expedientes procesados
          </p>
        </div>
      </div>
    );
  }

  // Calculate summary stats with safe accessors
  const totalProcessedSum = activeUsers.reduce((sum, u) => sum + safeNumber(u.total_processed), 0);
  const totalCompliantSum = activeUsers.reduce(
    (sum, u) => sum + safeNumber(u.total_compliant),
    0
  );
  const usersAboveTarget = activeUsers.filter(
    (u) => safeNumber(u.overall_compliance_pct) >= 85
  ).length;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-medium mb-4 text-gray-700">{title}</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {activeUsers.map((user) => (
          <UserCard key={user.user_id} user={user} />
        ))}
      </div>

      {/* Summary Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm">
          <div>
            <div className="font-semibold text-gray-900">
              {activeUsers.length}
            </div>
            <div className="text-gray-500">Usuarios Activos</div>
          </div>
          <div>
            <div className="font-semibold text-gray-900">
              {totalProcessedSum.toLocaleString()}
            </div>
            <div className="text-gray-500">Total Procesados</div>
          </div>
          <div>
            <div className="font-semibold text-green-600">
              {totalCompliantSum.toLocaleString()}
            </div>
            <div className="text-gray-500">En SLA</div>
          </div>
          <div>
            <div className="font-semibold text-blue-600">{usersAboveTarget}</div>
            <div className="text-gray-500">Cumplen Meta (85%)</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FolderSLAUserCards;
